import React, {
  memo,
  ReactNode,
  useState,
  useEffect,
  useRef,
  Fragment,
} from "react";
import ActionButton from "./Buttons/ActionButton";
import DropdownItem from "./DropdownItem";
import { NavLink } from "react-router-dom";
import { ReactComponent as SettingsIcon } from "../assets/icons/settings.svg";
import { useNotifications, useNotification } from "../Api/newSockets.api";

type Props = {
  icon: ReactNode;
  className?: string;
  label?: string;
};

function NotificationsButton(props: Props) {
  const { notifications } = useNotifications();
  const { markAllRead, clearAll } = useNotification();
  const notificationLimit = 3;

  const [expanded, setexpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const toggleMenu = (e: React.MouseEvent<HTMLElement>) => {
    setexpanded(!expanded);
  };

  const collapse = (e: any) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setexpanded(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", collapse);
    return () => {
      document.removeEventListener("click", collapse);
    };
  });

  const filterUnreadNotifications = () => {
    if (notifications) {
      return notifications.notifications
        .filter((notif) => notif.readStatus === false)
        .slice(0, notificationLimit);
    }
    return [];
  };

  const handleMarkAllRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAllRead();
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearAll();
  };

  const hasNotifications = notifications && notifications.notifications?.length > 0;
  const hasUnread = notifications && notifications.unreadNotificationsCount > 0;

  return (
    <div className="drop-down-button" onClick={toggleMenu} ref={ref}>
      {notifications && notifications.unreadNotificationsCount > 0 && (
        <div className="dropdown-button-badge">
          {notifications.unreadNotificationsCount}
        </div>
      )}
      <ActionButton
        icon={props.icon}
        label={props.label}
        className={props.className}
      />
      {expanded && (
        <div className="drop-down-menu">
          <Fragment>
            {/* Action buttons at top */}
            {hasNotifications && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "0.5rem 1rem",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  gap: "0.5rem",
                }}
              >
                <button
                  onClick={handleMarkAllRead}
                  disabled={!hasUnread}
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(83, 189, 228, 0.5)",
                    color: hasUnread ? "#53bde4" : "#666",
                    padding: "0.35rem 0.6rem",
                    borderRadius: "4px",
                    fontSize: "0.7rem",
                    cursor: hasUnread ? "pointer" : "default",
                    opacity: hasUnread ? 1 : 0.5,
                    flex: 1,
                  }}
                >
                  Mark all read
                </button>
                <button
                  onClick={handleClearAll}
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255, 107, 107, 0.5)",
                    color: "#ff6b6b",
                    padding: "0.35rem 0.6rem",
                    borderRadius: "4px",
                    fontSize: "0.7rem",
                    cursor: "pointer",
                    flex: 1,
                  }}
                >
                  Clear all
                </button>
              </div>
            )}

            {notifications && notifications.unreadNotificationsCount === 0 && (
              <div style={{ margin: "1.125rem", fontWeight: "700" }}>
                No new notifications
              </div>
            )}
            {hasUnread && (
              <Fragment>
                {filterUnreadNotifications().map((data) => (
                  <DropdownItem
                    key={data.id}
                    label={data.title}
                    to="/notifications"
                    bottomBorder={true}
                    icon={<SettingsIcon />}
                    description={data.description}
                    time={data.dateCreated}
                  />
                ))}
              </Fragment>
            )}

            <NavLink to="/notifications">
              <div
                className="view-all-notifications"
                style={{
                  border: "none",
                  fontSize: "0.7rem",
                  textDecoration: "underline",
                  width: "100%",
                  color: "#888",
                  marginTop: "0.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                View all notifications
              </div>
            </NavLink>
          </Fragment>
        </div>
      )}
    </div>
  );
}

function arePropsEqual(prevProps: any, nextProps: any) {
  return prevProps.label === nextProps.label;
}

export default memo(NotificationsButton, arePropsEqual);
